import React, { useState, useRef } from 'react';
import { importOrdersCSV, OrderImportResult } from '../../api/orders';
import { notifyPendingOrdersChanged } from '../../hooks/usePendingOrders';
import { colors, spacing, typography, typographyFlat, borderRadius, colorExtensions } from '../../styles/tokens';

interface OrderImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const OrderImportModal: React.FC<OrderImportModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<OrderImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setError(null);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setError(null);

    try {
      const result = await importOrdersCSV(selectedFile);
      setUploadResult(result);

      if (result.success) {
        notifyPendingOrdersChanged();
        // Auto-close after 2 seconds on success
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 2000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import orders');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setError(null);
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: `${colors.text}80`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: colors.white,
        borderRadius: borderRadius.lg,
        padding: spacing.xl,
        maxWidth: `calc(${spacing.lg} * 15)`,
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: spacing.lg
        }}>
          <h2 style={{
            fontSize: typography.h2.fontSize,
            fontWeight: typography.h2.fontWeight,
            color: colors.text,
            margin: 0
          }}>
            Import Orders
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: typographyFlat.fontSize.xl,
              cursor: 'pointer',
              color: colors.textSecondary,
              padding: 0,
              width: spacing.xl,
              height: spacing.xl
            }}
          >
            ×
          </button>
        </div>

        {/* Instructions */}
        {!uploadResult && (
          <div style={{
            backgroundColor: colors.backgroundAlt,
            border: `1px solid ${colors.border}`,
            borderRadius: borderRadius.md,
            padding: spacing.md,
            marginBottom: spacing.lg
          }}>
            <h4 style={{
              fontSize: typography.body.fontSize,
              fontWeight: typographyFlat.fontWeight.semibold,
              color: colors.text,
              margin: 0,
              marginBottom: spacing.sm
            }}>
              📋 Instructions
            </h4>
            <ul style={{
              margin: 0,
              paddingLeft: spacing.lg,
              fontSize: typography.small.fontSize,
              color: colors.textSecondary,
              lineHeight: '1.6'
            }}>
              <li>Export orders from TCGplayer as CSV</li>
              <li>File must include: Order ID, Date, Product Name, Quantity, Amounts</li>
              <li>Orders will be checked for duplicates before import</li>
              <li>You'll see a summary of successful and failed imports</li>
            </ul>
          </div>
        )}

        {/* Privacy Notice */}
        {!uploadResult && (
          <div style={{
            backgroundColor: `${colors.warning}26`,
            border: `1px solid ${colorExtensions.warningDark}`,
            borderRadius: borderRadius.md,
            padding: spacing.md,
            marginBottom: spacing.lg
          }}>
            <div style={{
              fontSize: typography.small.fontSize,
              color: colorExtensions.warningDark,
              lineHeight: '1.6'
            }}>
              ⚠️ <strong>Privacy Notice:</strong> This CSV may contain customer names and addresses. 
              By importing, you confirm you have appropriate consent to store this data.
            </div>
          </div>
        )}

        {/* File Selection */}
        {!uploadResult && (
          <div style={{ marginBottom: spacing.lg }}>
            <label style={{
              display: 'block',
              fontSize: typography.small.fontSize,
              fontWeight: typographyFlat.fontWeight.medium,
              color: colors.text,
              marginBottom: spacing.sm
            }}>
              Select CSV File
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <div style={{ display: 'flex', gap: spacing.sm }}>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                style={{
                  flex: 1,
                  padding: spacing.md,
                  backgroundColor: colors.backgroundAlt,
                  border: `2px dashed ${colors.border}`,
                  borderRadius: borderRadius.md,
                  fontSize: typography.body.fontSize,
                  color: colors.text,
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  textAlign: 'center'
                }}
              >
                {selectedFile ? selectedFile.name : 'Choose File...'}
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={{
            padding: spacing.md,
            backgroundColor: colors.errorLight,
            border: `1px solid ${colors.error}`,
            borderRadius: borderRadius.md,
            marginBottom: spacing.lg,
            color: colors.error,
            fontSize: typography.body.fontSize
          }}>
            ⚠️ {error}
          </div>
        )}

        {/* Upload Result */}
        {uploadResult && (
          <div style={{
            backgroundColor: uploadResult.success ? `${colors.success}19` : `${colors.error}19`,
            border: `1px solid ${uploadResult.success ? colorExtensions.successDark : colorExtensions.errorDark}`,
            borderRadius: borderRadius.md,
            padding: spacing.lg,
            marginBottom: spacing.lg
          }}>
            <h3 style={{
              fontSize: typography.body.fontSize,
              fontWeight: typographyFlat.fontWeight.semibold,
              color: uploadResult.success ? colorExtensions.successDark : colorExtensions.errorDark,
              margin: 0,
              marginBottom: spacing.md
            }}>
              {uploadResult.success ? '✅ Import Successful' : '⚠️ Import Completed with Errors'}
            </h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: spacing.md,
              marginBottom: spacing.md
            }}>
              <div>
                <div style={{
                  fontSize: typography.small.fontSize,
                  color: colors.textSecondary,
                  marginBottom: spacing.xs
                }}>
                  Total Orders
                </div>
                <div style={{
                  fontSize: typography.h2.fontSize,
                  fontWeight: typography.h2.fontWeight,
                  color: colors.text
                }}>
                  {uploadResult.total_orders}
                </div>
              </div>

              <div>
                <div style={{
                  fontSize: typography.small.fontSize,
                  color: colors.textSecondary,
                  marginBottom: spacing.xs
                }}>
                  Imported
                </div>
                <div style={{
                  fontSize: typography.h2.fontSize,
                  fontWeight: typography.h2.fontWeight,
                  color: colorExtensions.successDark
                }}>
                  {uploadResult.imported_count}
                </div>
              </div>

              <div>
                <div style={{
                  fontSize: typography.small.fontSize,
                  color: colors.textSecondary,
                  marginBottom: spacing.xs
                }}>
                  Failed
                </div>
                <div style={{
                  fontSize: typography.h2.fontSize,
                  fontWeight: typography.h2.fontWeight,
                  color: uploadResult.failed_count > 0 ? colorExtensions.errorDark : colors.text
                }}>
                  {uploadResult.failed_count}
                </div>
              </div>

              <div>
                <div style={{
                  fontSize: typography.small.fontSize,
                  color: colors.textSecondary,
                  marginBottom: spacing.xs
                }}>
                  Items Synced
                </div>
                <div style={{
                  fontSize: typography.h2.fontSize,
                  fontWeight: typography.h2.fontWeight,
                  color: colors.text
                }}>
                  {uploadResult.total_items_synced}
                </div>
              </div>
            </div>

            {/* Error List */}
            {uploadResult.errors.length > 0 && (
              <div>
                <div style={{
                  fontSize: typography.small.fontSize,
                  fontWeight: typographyFlat.fontWeight.semibold,
                  color: colors.text,
                  marginBottom: spacing.sm
                }}>
                  Errors:
                </div>
                <div style={{
                  maxHeight: `calc(${spacing.lg} * 8.33)`,
                  overflowY: 'auto',
                  backgroundColor: colors.white,
                  border: `1px solid ${colors.border}`,
                  borderRadius: borderRadius.sm,
                  padding: spacing.sm
                }}>
                  {uploadResult.errors.map((err, idx) => (
                    <div
                      key={idx}
                      style={{
                        fontSize: typography.small.fontSize,
                        color: colors.error,
                        padding: spacing.xs,
                        borderBottom: idx < uploadResult.errors.length - 1 ? `1px solid ${colors.border}` : 'none'
                      }}
                    >
                      {err.external_id && <strong>{err.external_id}:</strong>} {err.error}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: spacing.md
        }}>
          {!uploadResult ? (
            <>
              <button
                onClick={handleClose}
                disabled={isUploading}
                style={{
                  padding: `${spacing.sm} ${spacing.lg}`,
                  backgroundColor: colors.white,
                  color: colors.text,
                  border: `1px solid ${colors.border}`,
                  borderRadius: borderRadius.md,
                  fontSize: typography.body.fontSize,
                  cursor: isUploading ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!selectedFile || isUploading}
                style={{
                  padding: `${spacing.sm} ${spacing.lg}`,
                  backgroundColor: !selectedFile || isUploading ? colors.backgroundAlt : colors.primary,
                  color: !selectedFile || isUploading ? colors.textSecondary : colors.white,
                  border: 'none',
                  borderRadius: borderRadius.md,
                  fontSize: typography.body.fontSize,
                  fontWeight: typographyFlat.fontWeight.medium,
                  cursor: !selectedFile || isUploading ? 'not-allowed' : 'pointer'
                }}
              >
                {isUploading ? 'Uploading...' : 'Import Orders'}
              </button>
            </>
          ) : (
            <button
              onClick={handleClose}
              style={{
                padding: `${spacing.sm} ${spacing.lg}`,
                backgroundColor: colors.primary,
                color: colors.white,
                border: 'none',
                borderRadius: borderRadius.md,
                fontSize: typography.body.fontSize,
                fontWeight: typographyFlat.fontWeight.medium,
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
